// src/DashboardLayout.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, NavLink, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './DashboardLayout.css';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import SucursalLogo from '../sucursales/SucursalLogo';
import SucursalSelector from './SucursalSelector';
import { avisarCierreSesion } from '../../auth/authService';

// Menú del administrador agrupado por propósito: [sección, [[ruta, ícono, texto], …]].
const MENU_ADMIN = [
  ['Operación', [
    ['Agenda', '/img/manana.png', 'Agenda'],   // primera opción: página inicial del administrador
    ['/Finanzas/Dashboard', '/img/casa.png', 'Dashboard'],
    ['Cierres', '/img/reporte.png', 'Cierres de caja'],
    ['Compras', '/img/reporte.png', 'Compras'],
    ['Gastos', '/img/reporte.png', 'Gastos'],
  ]],
  ['Finanzas', [
    ['PagosAdministrativos', '/img/pago-en-efectivo.png', 'Pagos administrativos'],
    ['CuentasPorPagar', '/img/repartidor.png', 'Cuentas por pagar'],
    ['CuentasEntreTaquerias', '/img/billetes-de-banco.png', 'Cuentas entre taquerías'],
    ['Bancos', '/img/transferencia.png', 'Bancos'],
  ]],
  ['Control y análisis', [
    ['Resultados', '/img/grafica.png', 'Resultados'],
    ['Reportes', '/img/grafica.png', 'Reportes'],
    ['Bitacora', '/img/expediente.png', 'Bitácora'],
  ]],
  ['Catálogos y configuración', [
    ['Productos', '/img/factura.png', 'Productos'],
    ['Proveedores', '/img/repartidor.png', 'Proveedores'],
    ['Sucursales', '/img/que-taco.jpg', 'Sucursales'],
    ['Usuarios', '/img/agregaru.png', 'Usuarios'],
  ]],
];

function DashboardLayout() {
  const navigate = useNavigate();
  const { sucursalId, sucursalActiva, setSucursalId, sucursales, me, listo, estadoSesion, reintentar, isAdmin: adminFromSession } = useSucursalActiva();
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [menuUsuarioAbierto, setMenuUsuarioAbierto] = useState(false);

  // Nombre de usuario mostrado
  const username = me.username || localStorage.getItem('username') || 'Usuario';

  const isAdmin = adminFromSession;

  const path = location.pathname.toLowerCase();

  // Drawer (≤1100px, mismo breakpoint del CSS): se cierra al navegar, con el fondo, con Escape o con el botón de cerrar.
  // En escritorio el menú es fijo y la clase "open" no tiene efecto. Al cerrar, el foco vuelve al botón que lo abre.
  const triggerRef = useRef(null);
  const sidebarRef = useRef(null);
  const cerrarMenu = useCallback(() => {
    setIsSidebarOpen(false);
    if (sidebarRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
  }, []);
  useEffect(() => { cerrarMenu(); }, [location.pathname, cerrarMenu]);
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') cerrarMenu(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isSidebarOpen, cerrarMenu]);
  // Tocar una opción que navega (también la pantalla actual) cierra el drawer; títulos de sección y otros elementos no.
  const alTocarMenu = (e) => { if (e.target.closest('a[href]')) cerrarMenu(); };

  // Bloqueo de scroll cuando el drawer móvil está abierto
  useEffect(() => {
    document.documentElement.classList.toggle('drawer-open', isAdmin && isSidebarOpen);
    return () => document.documentElement.classList.remove('drawer-open');
  }, [isAdmin, isSidebarOpen]);

  const handleLogout = () => {
    avisarCierreSesion();
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    navigate('/login', { replace: true });
  };

  // Las pantallas se montan cuando ya se restauró la sucursal del administrador (no consultan antes "Todas").
  // Nada de la app (menú, pantallas) se monta con una sesión incompleta.
  if (estadoSesion === 'unauthenticated') return <Navigate to="/login" replace />;
  if (estadoSesion === 'error') return (
    <div className="cajero-container sesion-estado" role="alert">
      <p>No fue posible cargar la información de tu sesión.</p>
      <button type="button" className="sesion-reintentar" onClick={reintentar}>Reintentar</button>
    </div>
  );
  if (!listo) return <div className="cajero-container sesion-estado" role="status">Cargando sesión...</div>;

  // Al cambiar de sucursal el cajero vuelve a Caja: una caja o cuadre abierto pertenece a la sucursal anterior.
  const cambiarSucursalCajero = (id) => { if (id === sucursalId) return; setSucursalId(id); navigate('/Finanzas/Caja', { replace: true }); };

  if (!isAdmin) return (
    <div className="cajero-container">
      <Helmet>
        <link rel="stylesheet" href="https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap" />
      </Helmet>
      <header className="cajero-header">
        <div className="cajero-identidad">
          <img className="cajero-sistemi" src="/img/sistemi-icono.png" alt="SISTEMI" />
          <span className="cajero-separador" aria-hidden="true" />
          {/* Cajero con varias sucursales asignadas: elige la sucursal actual (solo entre las suyas). */}
          {sucursales.length > 1
            ? <div className="cajero-sucursal cajero-sucursal--selector"><span id="cajero-sucursal-label" className="sr-only">Sucursal actual</span>
              <SucursalSelector id="cajero-sucursal" sucursales={sucursales} value={sucursalId} conTodas={false} onChange={cambiarSucursalCajero} /></div>
            : <div className="cajero-sucursal"><SucursalLogo sucursal={sucursalActiva} size={34} decorative />
              <strong>{sucursalActiva?.nombre || 'Sucursal'}</strong></div>}
        </div>
        <div className="cajero-usuario">
          <button type="button" className="cajero-usuario-btn" aria-expanded={menuUsuarioAbierto}
            aria-haspopup="menu" onClick={() => setMenuUsuarioAbierto((v) => !v)}>
            <span className="cajero-usuario-inicial" aria-hidden="true">{username.charAt(0).toUpperCase()}</span>
            <span className="cajero-usuario-nombre">{username}</span>
            <span aria-hidden="true">⌄</span>
          </button>
          {menuUsuarioAbierto && <><button type="button" className="cajero-menu-cerrar" aria-label="Cerrar menú de usuario"
            onClick={() => setMenuUsuarioAbierto(false)} /><div className="cajero-usuario-menu" role="menu">
            <button type="button" role="menuitem" onClick={handleLogout}>Salir</button>
          </div></>}
        </div>
      </header>
      <main className="cajero-content"><Outlet key={sucursalId} /></main>
    </div>
  );

  return (
    <div className="admin-container">
      <Helmet>
        <link rel="stylesheet" href="https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap"
        />
      </Helmet>

      {/* Sidebar */}
      <aside ref={sidebarRef} id="sidebar" className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sb-header">
          {/* Cerrar drawer en móvil */}
          <button
            type="button"
            className="menu-btn"
            onClick={cerrarMenu}
            aria-label="Cerrar menú"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {/* Arriba siempre el logo de SISTEMI; la sucursal se identifica en el selector. */}
          <div className="brand">
            <img src="/img/sistemi-logo.png" alt="SISTEMI" className="sidebar-sistemi-logo" />
          </div>
        </div>

        {isAdmin
          ? <div className="sidebar-sucursal"><label id="sidebar-sucursal-label" htmlFor="sidebar-sucursal">Sucursal actual</label><SucursalSelector sucursales={sucursales} value={sucursalId} onChange={setSucursalId} /></div>
          : sucursalActiva && <div className="sidebar-sucursal"><span className="sidebar-sucursal-label">Sucursal actual</span><div className="sucursal-actual"><SucursalLogo sucursal={sucursalActiva} size={32} decorative /><span>{sucursalActiva.nombre}</span></div></div>}

        <nav id="sidebar-menu" className="menu-container" onClick={alTocarMenu}>
          <ul className="menu">
            {isAdmin ? <>
              {/* ===== Administrador: secciones con título discreto (mismas rutas, íconos y permisos de antes) ===== */}
              {MENU_ADMIN.map(([seccion, items]) => (
                <li key={seccion} className="menu-seccion" role="presentation">
                  <span className="menu-seccion-titulo" aria-hidden="true">{seccion}</span>
                  <ul className="menu menu-grupo" aria-label={seccion}>
                    {items.map(([to, icono, texto]) => (
                      <li className="menu-item" key={to}>
                        <NavLink to={to} className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}>
                          <img src={icono} alt="" />
                          <span>{texto}</span>
                        </NavLink>
                      </li>))}
                  </ul>
                </li>))}
            </> : <>
              {/* Cajero: una sola entrada. */}
              <li className="menu-item">
                <NavLink to="/Finanzas/Caja" className={({ isActive }) => `menu-link ${isActive || path.includes('/registrarcierre') || path.includes('/aperturaturno') ? 'active' : ''}`}>
                  <img src="/img/factura.png" alt="" />
                  <span>Caja</span>
                </NavLink>
              </li>
            </>}
          </ul>
        </nav>

        {/* Footer del sidebar: cerrar sesión */}
        <div className="sb-footer">
          <div className="footer-actions">
            <button
              onClick={handleLogout}
              className="logout-button"
              type="button"
              title="Cerrar sesión"
            >
              <i className="bx bx-log-out" />
              <span>Salir</span>
            </button>
          </div>
          {/* Crédito discreto del desarrollador (no compite con el logo de la sucursal). */}
          <p className="sb-credito">
            <img src="/img/sistemi-icono.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span>Construido por SISTEMI</span>
          </p>
        </div>
      </aside>

      {/* Backdrop para cerrar tocando fuera (solo en móvil) */}
      <div
        className={`backdrop ${isSidebarOpen ? 'show' : ''}`}
        onClick={cerrarMenu}
        aria-hidden="true"
      />

      {/* Botón hamburguesa (solo móvil) */}
      <button
        type="button"
        ref={triggerRef}
        className="mobile-trigger"
        onClick={() => setIsSidebarOpen(true)}
        aria-label="Abrir menú"
        aria-expanded={isSidebarOpen}
        aria-controls="sidebar"
      >
        <i className="bx bx-menu"></i>
      </button>

      {/* Contenido */}
      <main className="admin-content">
        {/* Barra de usuario arriba */}
        <header className="userbar">
          <div className="actions">
            {isAdmin && <button className="icon-btn" title="Notificaciones">
              <i className="bx bx-bell"></i>
            </button>}
          </div>
          <div className="user-chip">
            <img src="/img/perfilusuario.png" alt="perfil-usuario" />
            <div className="meta">
              <strong>{username}</strong>
              <small>Administrador</small>
            </div>
          </div>
        </header>

        <div className="content-card">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;

